/**
 * Sold-out-size modal (Essential <-> Timeless) — logic for snippets/size-alternatives-modal.liquid.
 *
 * Opens when the shopper clicks a SIZE radio that is sold out on the current product
 * (the theme marks it with the `disabled` class, or the `disabled` attribute when the
 * back-in-stock toggle is off) and at least one alternative product has that size in stock.
 * Not the "Notify me" form: this runs first and independently of it.
 *
 * Cards are rendered by Liquid; each carries its variants as
 * [size, colour, variantId, available, price, compareAt, skuColourCode].
 * Rule (user, 2026-09-11): if a twin in the EXACT colour has the size, show ONLY that one
 * product; only when no exact colour is in stock, show the SIMILAR-colour twins (per the
 * generated SKU-colour-code map in snippets/size-alternatives-colour-map.liquid, tagged
 * "Similar colour"). Never the whole metafield list, never unrelated colours. The metafield
 * list is ordered exact-then-similar on the one-colour stores; the ranking here makes UK's
 * merged multi-colour products behave the same.
 *
 * Reuses the theme's global trapFocus / removeTrapFocus (assets/global.js) when present.
 */
(() => {
  if (customElements.get('size-alternatives-modal')) return;

  const norm = (value) => String(value == null ? '' : value).trim().toLowerCase();
  const colourCode = (sku) => {
    const parts = String(sku || '').split('-');
    return parts.length > 1 ? parts[1].slice(0, 4).toUpperCase() : '';
  };

  class SizeAlternativesModal extends HTMLElement {
    connectedCallback() {
      // The element is moved to <body> when opened, which re-runs connectedCallback.
      if (this._initialised) return;
      this._initialised = true;

      this.dialog = this.querySelector('[data-sa-dialog]');
      this.list = this.querySelector('[data-sa-items]');
      this.items = Array.from(this.querySelectorAll('[data-sa-item]'));
      this.sizeOption = norm(this.dataset.sizeOption || 'size');
      this.colorOption = norm(this.dataset.colorOption || 'color');
      this.productInfo =
        document.getElementById(`MainProduct-${this.dataset.section}`) ||
        this.closest('product-info') ||
        this.closest('.shopify-section');
      this.openedById = null;

      this.colourMap = {};
      try {
        const mapEl = this.querySelector('[data-sa-colour-map]');
        this.colourMap = mapEl ? JSON.parse(mapEl.textContent || '{}') : {};
      } catch (error) {
        this.colourMap = {};
      }

      this.onProductClick = this.onProductClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      // Delegated: product-variant-selects is swapped on every variant change, the
      // <product-info> wrapper is stable.
      (this.productInfo || document).addEventListener('click', this.onProductClick);

      this.querySelectorAll('[data-sa-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      this.addEventListener('click', (event) => {
        if (event.target === this || event.target.hasAttribute('data-sa-overlay')) this.close();
      });
    }

    // ---- trigger -------------------------------------------------------------------------

    onProductClick(event) {
      const input = this.resolveSizeInput(event.target);
      if (!input) return;

      const soldOut =
        input.classList.contains('disabled') ||
        input.disabled ||
        input.getAttribute('aria-disabled') === 'true' ||
        input.dataset.soldout === 'true';
      if (!soldOut) return;

      const size = (input.value || '').trim();
      if (!size) return;

      const colour = this.selectedColour();
      const shown = this.populate(size, colour, this.selectedColourCode(colour));
      if (!shown) return;

      this.openedById = input.id || null;
      this.open(size, shown);
    }

    /** The size radio behind a click, or null when the click is not on a size option. */
    resolveSizeInput(target) {
      if (!(target instanceof Element)) return null;

      let input = target.closest('input[type="radio"]');
      if (!input) {
        const label = target.closest('label[for]');
        if (!label) return null;
        input = document.getElementById(label.htmlFor);
        // An enabled input receives its own (bubbling) click right after the label's —
        // handle that one instead so the modal opens once. A disabled input never does.
        if (!input || !input.disabled) return null;
      }
      if (!input.closest('product-variant-selects')) return null;
      // product-option.liquid names inputs "<Option name>-<position>", e.g. "Size-1".
      if (!norm(input.name).startsWith(`${this.sizeOption}-`)) return null;
      return input;
    }

    /** Colour currently selected on the page ('' when the product has no colour option). */
    selectedColour() {
      const root = this.productInfo || document;
      const checked = root.querySelectorAll('product-variant-selects input[type="radio"]:checked');
      for (const input of checked) {
        if (norm(input.name).startsWith(`${this.colorOption}-`)) return (input.value || '').trim();
      }
      const select = root.querySelector(`product-variant-selects select[name$="[${this.dataset.colorOption}]"]`);
      return select ? (select.value || '').trim() : '';
    }

    /** SKU colour code of the selected colour, read from the picker's own variant JSON. */
    selectedColourCode(colour) {
      const root = this.productInfo || document;
      const el = root.querySelector('product-variant-selects [data-all-variants]');
      if (!el) return '';
      let variants = [];
      try {
        variants = JSON.parse(el.textContent || '[]');
      } catch (error) {
        return '';
      }
      const key = norm(colour);
      const match = variants.find((v) =>
        Array.isArray(v.options) && (!key || v.options.some((o) => norm(o) === key)) && v.sku
      );
      return match ? colourCode(match.sku) : '';
    }

    // ---- content -------------------------------------------------------------------------

    /** Show the cards that have `size` in the exact or a similar colour; returns the count. */
    populate(size, colour, code) {
      const sizeKey = norm(size);
      const colourKey = norm(colour);
      const similar = new Set(code && this.colourMap[code] ? this.colourMap[code] : []);
      const ranked = [];

      this.items.forEach((item, index) => {
        let variants = [];
        try {
          variants = JSON.parse(item.dataset.variants || '[]');
        } catch (error) {
          variants = [];
        }
        const withSize = variants.filter((v) => norm(v[0]) === sizeKey && v[3]);
        let tier = -1;
        let pick = null;
        if (withSize.length) {
          pick = withSize.find((v) => (colourKey && norm(v[1]) === colourKey) || (code && v[6] === code)) || null;
          if (pick) {
            tier = 0;
          } else if (!colourKey && !code) {
            pick = withSize[0]; // product without a colour option: any stock counts
            tier = 0;
          } else {
            pick = withSize.find((v) => v[6] && similar.has(v[6])) || null;
            if (pick) tier = 1;
          }
        }
        if (!pick) {
          item.hidden = true;
          return;
        }

        const link = item.querySelector('[data-sa-link]');
        if (link) {
          const base = link.dataset.baseHref || link.getAttribute('href') || '';
          link.href = `${base}${base.includes('?') ? '&' : '?'}variant=${pick[2]}`;
        }
        const colourEl = item.querySelector('[data-sa-colour]');
        if (colourEl) {
          colourEl.textContent = pick[1] || '';
          colourEl.hidden = !pick[1];
        }
        const similarEl = item.querySelector('[data-sa-similar-colour]');
        if (similarEl) similarEl.hidden = tier !== 1;
        item.classList.toggle('sa-modal__item--other-colour', tier === 1);

        const priceEl = item.querySelector('[data-sa-price]');
        if (priceEl && pick[4]) priceEl.textContent = pick[4];
        const compareEl = item.querySelector('[data-sa-compare]');
        if (compareEl) {
          compareEl.textContent = pick[5] || '';
          compareEl.hidden = !pick[5];
        }

        item.hidden = false;
        ranked.push({ item, tier, index });
      });

      // User rule (2026-09-11): never the whole list at once. If an exact-colour twin has the
      // size, show ONLY that one product; only when no exact colour is in stock, show the
      // similar-colour twins (metafield order).
      ranked.sort((a, b) => a.tier - b.tier || a.index - b.index);
      const exact = ranked.filter((r) => r.tier === 0);
      const shown = exact.length ? exact.slice(0, 1) : ranked.filter((r) => r.tier === 1);
      ranked.forEach((r) => {
        if (!shown.includes(r)) r.item.hidden = true;
      });
      if (this.list) shown.forEach(({ item }) => this.list.appendChild(item));

      this.querySelectorAll('[data-sa-text]').forEach((el) => {
        el.textContent = (el.dataset.saText || '').replace(/%SIZE%/g, size);
      });

      return shown.length;
    }

    // ---- open / close --------------------------------------------------------------------

    open(size, count) {
      // Portal to <body> so position:fixed is not trapped by a transformed ancestor.
      if (this.parentElement !== document.body) document.body.appendChild(this);

      this.hidden = false;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);
      if (typeof trapFocus === 'function' && this.dialog) {
        trapFocus(this.dialog, this.dialog);
      } else if (this.dialog) {
        this.dialog.focus({ preventScroll: true });
      }

      try {
        (window.dataLayer = window.dataLayer || []).push({
          event: 'size_alternatives_open',
          sa_product: this.dataset.productHandle || '',
          sa_size: size,
          sa_count: count,
        });
      } catch (error) {
        /* analytics only */
      }
    }

    close() {
      if (this.hidden) return;
      this.hidden = true;
      document.removeEventListener('keydown', this.onKeydown);

      // The variant picker was re-rendered meanwhile: look the trigger up by id again.
      const trigger = this.openedById ? document.getElementById(this.openedById) : null;
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(trigger);
      } else if (trigger) {
        trigger.focus({ preventScroll: true });
      }

      // Only release body scroll when no sibling modal (size chart / recommender) is open.
      if (!document.querySelector('.sg-modal:not([hidden]), .sr-modal:not([hidden])')) {
        document.body.style.overflow = '';
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }
  }

  customElements.define('size-alternatives-modal', SizeAlternativesModal);
})();
